import React, { useEffect, useRef, useState } from "react";
import { api, imageUrl } from "../lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Cinematic ken-burns cross-fade slideshow of the top 5 cars from the inventory.
 * Acts as the "video" background of the hero — real MP4 assets are avoided so
 * the site stays fast and the images come from the live inventory automatically.
 */
export const HeroSlideshow = ({ fallback }) => {
  const [images, setImages] = useState([]);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const resumeRef = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    // Consider it a swipe only if horizontal movement dominates and is significant
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4) {
      goTo(dx < 0 ? 1 : -1);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/cars", { params: { include_sold: false } });
        const list = Array.isArray(r.data) ? r.data : [];
        const withImg = list
          .filter((c) => c.images && c.images[0])
          .sort((a, b) => {
            const af = a.is_featured === true, bf = b.is_featured === true;
            if (af !== bf) return bf - af;
            return (a.featured_order ?? 0) - (b.featured_order ?? 0);
          })
          .slice(0, 5)
          .map((c) => imageUrl(c.images[0].storage_path));
        if (alive && withImg.length > 0) setImages(withImg);
      } catch { /* fallback used */ }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (images.length <= 1 || paused) return;
    timer.current = setInterval(() => {
      setActive((i) => (i + 1) % images.length);
    }, 3500);
    return () => clearInterval(timer.current);
  }, [images.length, paused]);

  const goTo = (dir) => {
    setPaused(true);
    setActive((i) => {
      const n = images.length || 1;
      return (i + dir + n) % n;
    });
    // resume autoplay after a short delay
    clearTimeout(resumeRef.current);
    resumeRef.current = setTimeout(() => setPaused(false), 8000);
  };

  const list = images.length > 0 ? images : (fallback ? [fallback] : []);
  const showArrows = list.length > 1;

  return (
    <div
      className="absolute inset-0 overflow-hidden touch-pan-y"
      data-testid="hero-slideshow"
      onTouchStart={showArrows ? onTouchStart : undefined}
      onTouchEnd={showArrows ? onTouchEnd : undefined}
    >
      {list.map((src, idx) => (
        <div
          key={src + idx}
          className="absolute inset-0 hero-slide"
          style={{
            backgroundImage: `url('${src}')`,
            opacity: idx === active ? 1 : 0,
            animationName: idx === active ? "hero-kenburns" : "none",
          }}
        />
      ))}
      {/* subtle dark vignette to keep hero copy legible */}
      <div className="absolute inset-0 bg-gradient-to-br from-midnight via-midnight/85 to-midnight/60 pointer-events-none" />

      {showArrows && false && (
        <>
          <button
            type="button"
            onClick={() => goTo(-1)}
            aria-label="Vettura precedente"
            data-testid="hero-prev-btn"
            className="hidden absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-10 h-11 w-11 md:h-14 md:w-14 items-center justify-center bg-white/10 hover:bg-gold hover:text-midnight text-white border border-white/20 hover:border-gold backdrop-blur-md transition-colors rounded-sm"
          >
            <ChevronLeft size={22} strokeWidth={2.2} />
          </button>
          <button
            type="button"
            onClick={() => goTo(1)}
            aria-label="Vettura successiva"
            data-testid="hero-next-btn"
            className="hidden absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-10 h-11 w-11 md:h-14 md:w-14 items-center justify-center bg-white/10 hover:bg-gold hover:text-midnight text-white border border-white/20 hover:border-gold backdrop-blur-md transition-colors rounded-sm"
          >
            <ChevronRight size={22} strokeWidth={2.2} />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-1.5" data-testid="hero-dots">
            {list.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => { setActive(idx); setPaused(true); clearTimeout(resumeRef.current); resumeRef.current = setTimeout(() => setPaused(false), 8000); }}
                aria-label={`Vai alla ${idx + 1}`}
                data-testid={`hero-dot-${idx}`}
                className={`h-1.5 transition-all ${idx === active ? "w-8 bg-gold" : "w-4 bg-white/40 hover:bg-white/70"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default HeroSlideshow;
